import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import userSettingsService from '../services/userSettings.service';
import { Tooltip } from './ui/Tooltip';

// Color definitions
const STATUS_COLORS = {
  grey: { bg: 'bg-gray-400', border: 'border-gray-500', hex: '#9CA3AF' },
  yellow: { bg: 'bg-yellow-400', border: 'border-yellow-500', hex: '#FACC15' },
  orange: { bg: 'bg-orange-400', border: 'border-orange-500', hex: '#FB923C' },
  red: { bg: 'bg-red-500', border: 'border-red-600', hex: '#EF4444' },
  green: { bg: 'bg-green-500', border: 'border-green-600', hex: '#22C55E' },
} as const;

type StatusColor = keyof typeof STATUS_COLORS;

interface DriverStatusDotProps {
  driverId: string;
}

// LocalStorage cache keys
const CACHE_COLORS_KEY = 'driver_status_colors_cache';
const CACHE_LABELS_KEY = 'driver_status_labels_cache';

// Get today's date in CST timezone as YYYY-MM-DD
const getTodayCST = (): string => {
  const now = new Date();
  const cstOffset = -6; // CST is UTC-6 (ignoring DST for simplicity, or use -5 for CDT)
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  const cst = new Date(utc + (3600000 * cstOffset));
  return cst.toISOString().split('T')[0];
};

// Cache helpers
const getColorsFromCache = (): Record<string, { color: StatusColor; date: string }> => {
  try {
    const cached = localStorage.getItem(CACHE_COLORS_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch {
    return {};
  }
};

const saveColorsToCache = (colors: Record<string, { color: StatusColor; date: string }>) => {
  try {
    localStorage.setItem(CACHE_COLORS_KEY, JSON.stringify(colors));
  } catch (error) {
    console.error('Failed to save colors to cache:', error);
  }
};

const DEFAULT_LABELS: Record<StatusColor, string> = {
  grey: 'No Status',
  yellow: '',
  orange: '',
  red: '',
  green: '',
};

const getLabelsFromCache = (): Record<StatusColor, string> => {
  try {
    const cached = localStorage.getItem(CACHE_LABELS_KEY);
    const labels = cached ? JSON.parse(cached) : { ...DEFAULT_LABELS };
    // Always ensure grey is "No Status"
    labels.grey = 'No Status';
    return labels;
  } catch {
    return { ...DEFAULT_LABELS };
  }
};

const saveLabelsToCache = (labels: Record<StatusColor, string>) => {
  try {
    localStorage.setItem(CACHE_LABELS_KEY, JSON.stringify(labels));
  } catch (error) {
    console.error('Failed to save labels to cache:', error);
  }
};

// Global flag to ensure we only sync from API once per session
let apiSyncDone = false;
let apiSyncPromise: Promise<void> | null = null;

const syncFromApiOnce = async () => {
  if (apiSyncDone) return;
  if (apiSyncPromise) return apiSyncPromise;

  apiSyncPromise = (async () => {
    try {
      const [colorsSetting, labelsSetting] = await Promise.all([
        userSettingsService.getUserSetting('driver_status_colors'),
        userSettingsService.getUserSetting('driver_status_labels'),
      ]);

      // Sync colors from API
      if (colorsSetting?.setting_value) {
        const colors = colorsSetting.setting_value as Record<string, { color: StatusColor; date: string }>;
        saveColorsToCache(colors);
      }

      // Sync labels from API
      if (labelsSetting?.setting_value) {
        const apiLabels = labelsSetting.setting_value as Record<StatusColor, string>;
        saveLabelsToCache(apiLabels);
      }

      apiSyncDone = true;
    } catch (error) {
      console.error('Failed to sync driver status settings from API:', error);
    }
  })();

  return apiSyncPromise;
};

const DriverStatusDot: React.FC<DriverStatusDotProps> = ({ driverId }) => {
  // Initialize from cache immediately
  const [color, setColor] = useState<StatusColor>(() => {
    const today = getTodayCST();
    const cachedColors = getColorsFromCache();
    const driverData = cachedColors[driverId];
    return driverData && driverData.date === today ? driverData.color : 'grey';
  });

  const [isOpen, setIsOpen] = useState(false);
  const [labels, setLabels] = useState<Record<StatusColor, string>>(getLabelsFromCache);
  const [allDriverColors, setAllDriverColors] = useState<Record<string, { color: StatusColor; date: string }>>(getColorsFromCache);

  const dotRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Sync from API once per session (background, non-blocking)
  useEffect(() => {
    syncFromApiOnce().then(() => {
      // After sync, refresh state from cache
      const today = getTodayCST();
      const cachedColors = getColorsFromCache();
      const driverData = cachedColors[driverId];
      if (driverData && driverData.date === today) {
        setColor(driverData.color);
      }
      setAllDriverColors(cachedColors);
      setLabels(getLabelsFromCache());
    });
  }, [driverId]);

  // Handle click outside to close popup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        dotRef.current &&
        !dotRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Handle Escape key to close popup
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Save color for this driver
  const handleColorSelect = (newColor: StatusColor) => {
    const today = getTodayCST();
    const updatedColors = {
      ...allDriverColors,
      [driverId]: { color: newColor, date: today },
    };

    // Update state and cache immediately
    setColor(newColor);
    setAllDriverColors(updatedColors);
    saveColorsToCache(updatedColors);
    setIsOpen(false);

    // Save to API in background
    userSettingsService.saveUserSetting('driver_status_colors', updatedColors)
      .catch((error) => {
        console.error('Failed to save driver status color:', error);
      });
  };

  // Save label with debounce
  const handleLabelChange = useCallback((colorKey: StatusColor, value: string) => {
    const updatedLabels = { ...labels, [colorKey]: value };

    // Update state and cache immediately
    setLabels(updatedLabels);
    saveLabelsToCache(updatedLabels);

    // Debounce the API save
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      userSettingsService.saveUserSetting('driver_status_labels', updatedLabels)
        .catch((error) => {
          console.error('Failed to save driver status labels:', error);
        });
    }, 500);
  }, [labels]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Handle opening the popup - read latest labels from cache
  const handleOpen = () => {
    if (isOpen) {
      setIsOpen(false);
      return;
    }

    // Refresh labels from cache (in case another dot updated them)
    setLabels(getLabelsFromCache());
    setIsOpen(true);
  };

  // Calculate popup position
  const getPopupPosition = () => {
    if (!dotRef.current) return { top: 0, left: 0 };

    const rect = dotRef.current.getBoundingClientRect();
    const popupWidth = 220;
    const popupHeight = 200;

    let top = rect.bottom + 4;
    let left = rect.left;

    // Check if popup would go off right edge
    if (left + popupWidth > window.innerWidth - 8) {
      left = window.innerWidth - popupWidth - 8;
    }

    // Check if popup would go off bottom edge
    if (top + popupHeight > window.innerHeight - 8) {
      top = rect.top - popupHeight - 4;
    }

    return { top, left };
  };

  const currentLabel = labels[color];
  const colorConfig = STATUS_COLORS[color];

  return (
    <>
      <Tooltip content={currentLabel || null} placement="top" disabled={!currentLabel}>
        <button
          ref={dotRef}
          onClick={handleOpen}
          className={`w-3 h-3 rounded-full border-2 ${colorConfig.bg} ${colorConfig.border} cursor-pointer hover:scale-110 transition-transform focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500`}
          title={currentLabel || 'Set status color'}
          aria-label={`Driver status: ${color}${currentLabel ? ` - ${currentLabel}` : ''}`}
        />
      </Tooltip>

      {isOpen && createPortal(
        <div
          ref={popupRef}
          className="fixed bg-panel rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-3 z-[99999]"
          style={{
            top: `${getPopupPosition().top}px`,
            left: `${getPopupPosition().left}px`,
            width: '220px',
          }}
        >
          <div className="space-y-2">
            {(Object.keys(STATUS_COLORS) as StatusColor[]).map((colorKey) => {
              const config = STATUS_COLORS[colorKey];
              const isSelected = color === colorKey;

              return (
                <div key={colorKey} className="flex items-center gap-2">
                  <button
                    onClick={() => handleColorSelect(colorKey)}
                    className={`w-5 h-5 rounded-full border-2 ${config.bg} ${config.border} cursor-pointer hover:scale-110 transition-transform ${
                      isSelected ? 'ring-2 ring-offset-1 ring-blue-500' : ''
                    }`}
                    aria-label={`Select ${colorKey} status`}
                  />
                  {colorKey === 'grey' ? (
                    <span className="flex-1 px-2 py-1 text-sm text-muted">
                      No Status
                    </span>
                  ) : (
                    <input
                      type="text"
                      value={labels[colorKey]}
                      onChange={(e) => handleLabelChange(colorKey, e.target.value)}
                      placeholder={colorKey.charAt(0).toUpperCase() + colorKey.slice(1)}
                      className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-body focus:outline-none focus:ring-1 focus:ring-blue-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default DriverStatusDot;
